import { HttpClient } from '@angular/common/http';
import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import * as InvitesGet from '../../../server/routes/orgs/invites/get';
import * as OrgNamePatch from '../../../server/routes/orgs/name/patch';
import {
  generatePath,
  orgNameMaxLength,
} from '../../../server/services/utils.service';
import type { Org, OrgSpace, OrgUser } from '../../../server/types/org-types';
import { type DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { SpaceCreateDialogComponent } from '../space-create-dialog/space-create-dialog.component';
import { TitleService } from '../title.service';

@Component({
  selector: 'app-org-settings',
  templateUrl: './org-settings.component.html',
  styleUrls: ['./org-settings.component.scss'],
  standalone: false,
})
export class OrgSettingsComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private titleService = inject(TitleService);
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  org: DeepReadonly<Org>;
  accountId: string;
  users: { user: DeepReadonly<OrgUser>; invitedPending: boolean }[];
  archiveUsersShown = false;
  showShowArchivedUsersButton = false;
  spaces: DeepReadonly<OrgSpace[]>;
  isAdmin = false;

  name: string;
  canEditName = false;
  nameControl: FormControl<string>;
  nameChanged = false;
  nameUpdateState: 'none' | 'waiting' | 'complete' = 'none';

  pageState: 'waiting' | 'ready' | 'error' = 'waiting';

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.titleService.set('Organization settings');

    const sub = this.authUserService.userOrgObj.subscribe((userOrgObj) => {
      if (!userOrgObj) return; // This should not happen except at logout

      this.org = userOrgObj.org;
      this.users = this.org.users
        .filter((u) => this.archiveUsersShown || !u.archivedAt) // Don't show archived unless show button already pressed
        .map((user) => ({ user, invitedPending: false }));
      this.showShowArchivedUsersButton =
        !this.archiveUsersShown &&
        !!this.org.users.filter((u) => u.archivedAt).length;

      this.spaces = this.org.spaces;
      const orgId = this.org._id;
      this.isAdmin = userOrgObj.user.admin;

      // Name control
      this.name = this.org.name;
      this.canEditName = userOrgObj.user.admin;
      if (this.nameControl) {
        this.nameControl.setValue(this.name);
      } else {
        this.nameControl = new FormControl(this.name, {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.maxLength(orgNameMaxLength),
          ],
        });
        const sub1 = this.nameControl.valueChanges.subscribe((value) => {
          this.nameChanged = value !== this.name;
          if (this.nameChanged && this.nameUpdateState === 'complete') {
            this.nameUpdateState = 'none';
          }
        });
        this.subscriptions.add(sub1);
      }

      this.pageState = 'ready';

      if (this.isAdmin) {
        // only get invites on the first update to the userOrgObj
        const path = generatePath<InvitesGet.ReqParams>(
          '/api/orgs/:orgId/invites',
          { orgId }
        );
        const sub2 = this.http.get<InvitesGet.ResBody>(path).subscribe({
          next: (invites) => {
            invites.forEach((invite) => {
              const user = this.users.find((u) => u.user._id === invite.userId);
              if (user) user.invitedPending = true;
            });
          },
          error: (error) => {
            // TODO: Handle error
          },
        });
        this.subscriptions.add(sub2);
      }
    });
    this.subscriptions.add(sub);
  }

  showArchivedUsers() {
    this.archiveUsersShown = true;
    this.showShowArchivedUsersButton = false;

    this.org.users
      .filter((u) => u.archivedAt)
      .forEach((user) => {
        this.users.push({ user, invitedPending: false });
      });
  }

  saveName() {
    const uncleanName = this.nameControl.value;
    const name = uncleanName.trim();
    const path = generatePath<OrgNamePatch.ReqParams>('/api/orgs/:orgId/name', {
      orgId: this.org._id,
    });
    this.nameChanged = false;
    const body: OrgNamePatch.ReqBody = { name };
    this.nameUpdateState = 'waiting';
    const sub = this.http.patch<OrgNamePatch.ResBody>(path, body).subscribe({
      next: ({ org }) => {
        this.nameUpdateState = 'complete';
        this.authUserService.updateOrg({ org });
      },
      error: (err) => {
        this.nameUpdateState = 'none';
        this.snackBar.open(
          'ERROR: could not update organization name.',
          'Dismiss',
          { duration: 5000 }
        );
      },
    });
    this.subscriptions.add(sub);
  }

  createSpaceDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { orgId: this.org._id };
    this.dialog.open(SpaceCreateDialogComponent, dialogConfig);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
